<script setup lang="ts">
import BlogBreadcrumb from '@/components/blog/BlogBreadcrumb.vue'
import BlogCard from '@/components/blog/BlogCard.vue'
import BlogFAQ from '@/components/blog/BlogFaq.vue'
import type { Blog } from '@/data/blogs'
import { formatDateToISO, getBlogBySlug, getBlogFolderByCategory } from '@/data/blogs'
import { blogs } from '@/data/blogs'
import { getLatestBlogs } from '@/data/blogs'
import { easeOut, motion } from 'motion-v'

const route = useRoute()

// Get the slug from the URL (e.g., "why-restaurant-marketing-feels-complicated")
const slugFromUrl = route.path.split('/').pop()

const blog = getBlogBySlug(slugFromUrl?.toString() || '')

const lastThreeBlogs = getLatestBlogs(
  blogs.filter(blogItem => blogItem.slug !== slugFromUrl && blogItem.category === blog?.category),
  3,
)

const headlineVariants = {
  initial: { opacity: 0, y: '10%' },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: easeOut } },
}

const paragraphVariants = {
  initial: { opacity: 0, y: 20 },
  animate: (custom: unknown) => {
    const i = Number(custom)
    return {
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.05, duration: 0.4, ease: easeOut },
    }
  },
}

const blogVariants = {
  initial: { opacity: 0, y: 50 },
  animate: (custom: unknown) => {
    const i = Number(custom)
    return {
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.5, ease: easeOut },
    }
  },
}

const blogSchema = {
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  'headline': blog?.seo?.title,
  'image': [
    blog?.image,
  ],
  'datePublished': formatDateToISO(blog?.seo?.datePublished || ''),
  // 'dateModified': blog?.seo?.dateModified,
  // 'author': blog?.seo?.author?.map(a => ({
  //   '@type': a['@type'],
  //   'name': a.name,
  //   'url': a.url,
  // })) || [],
}

const faqSchema = computed(() => {
  if (!blog?.faq?.length) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': blog.faq.map(item => ({
      '@type': 'Question',
      'name': item.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': item.answer,
      },
    })),
  }
})

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  'itemListElement': [
    {
      '@type': 'ListItem',
      'position': 1,
      'name': 'Home',
      'item': `${import.meta.env.VITE_MONOLITH_URL}/`,
    },
    {
      '@type': 'ListItem',
      'position': 2,
      'name': 'Blog',
      'item': `${import.meta.env.VITE_MONOLITH_URL}/blog`,
    },
    {
      '@type': 'ListItem',
      'position': 3,
      'name': blog?.category,
      'item': `${import.meta.env.VITE_MONOLITH_URL}/blog?category=${encodeURIComponent(blog?.category || '')}`,
    },
    {
      '@type': 'ListItem',
      'position': 4,
      'name': blog?.title,
      'item': `${import.meta.env.VITE_MONOLITH_URL}/blog/${getBlogFolderByCategory(blog?.category || '')}/${slugFromUrl}`,
    },
  ],
}

useHead({
  link: [{
    rel: 'canonical',
    href: `${import.meta.env.VITE_MONOLITH_URL}/blog/${getBlogFolderByCategory(blog?.category || '')}/${slugFromUrl}`,
  }],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(blogSchema), // BlogPosting
    },
    blog?.faq?.length
      ? {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(faqSchema.value), // FAQPage
        }
      : null,
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(breadcrumbSchema),
    },
  ].filter(Boolean),
})

useSeoMeta({
  title: blog?.seo?.title,
  description: blog?.seo?.description,
  ogTitle: blog?.seo?.title,
  ogDescription: blog?.seo?.description,
  ogImage: blog?.seo?.ogImage,
})
</script>
<template>
  <div class="isolate">
    <LayoutNavbar />

    <div
      class="mb-0 bg-gradient-to-b from-[#f8f8f0] from-50% to-[#f8f8f0] pt-4  md:shadow-2xl md:shadow-black/10"
    >
      <section class="mx-auto mb-8 max-w-screen-xl px-4 md:px-8 lg:max-w-screen-xl xl:px-8">
        <BlogBreadcrumb
          :blog="blog as Blog"
        />
        <motion.h1
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-3xl font-medium text-gray-900 lg:text-4xl"
        >
          {{ blog?.title }}
        </motion.h1>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          <router-link
            :to="{ path: '/blog', query: { category: blog?.category } }"
            class="text-[#FF7118] underline"
          >
            {{ blog?.category }}
          </router-link> • {{ blog?.date }}
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          Key Takeaways (30 Seconds)
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-4 text-left text-slate-700"
        >
          Restaurant websites succeed when key information and next steps are clear, helping diners take action, like booking a reservation or placing an order.
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li><span>Getting the right visitors matters more than getting more traffic.</span></li>
          <li>
            <span>
              Clear, visible calls to action (CTAs) directly drive reservations, online orders, and catering inquiries.
            </span>
          </li>
          <li>
            <span>
              Diners decide fast—if key information isn’t obvious within seconds, they leave.
            </span>
          </li>
          <li>
            <span>
              Trust signals like reviews, photos, and updated menus influence decisions instantly.
            </span>
          </li>
          <li>
            <span>
              Fast mobile performance is critical. Slow websites lose customers.
            </span>
          </li>
        </motion.ul>

        <div class="mb-5 mt-8 flex flex-col items-stretch gap-4 md:flex-row">
          <motion.img
            :initial="{ opacity: 0, y: 50 }"
            :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } }"
            :src="blog?.image"
            :alt="blog?.alt"
            class=" w-full rounded-2xl object-cover drop-shadow-lg md:w-7/12"
          />

          <div class="w-full rounded-2xl border-4 border-[#FF711833] bg-white px-4 py-2 md:w-5/12">
            <motion.p
              :variants="paragraphVariants"
              :custom="0"
              initial="initial"
              while-in-view="animate"
              class="relative w-full text-xl leading-relaxed text-slate-700"
            >
              <svg
                width="35"
                height="28"
                viewBox="0 0 35 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="inline pb-1"
              >
                <path
                  d="M33.7768 1.80041e-06C34.496 1.86329e-06 35 0.546003 35 1.1585C35 1.59425 34.7463 2.0615 34.1303 2.38875C31.0013 4.046 27.3788 9.78425 27.3788 13.3228C32.6568 13.1618 34.9493 17.8413 34.9493 20.7743C34.9493 24.2848 31.9077 28 27.4102 28C21.7385 28 18.9577 23.5427 18.9577 18.7127C18.9578 8.246 30.9173 1.55042e-06 33.7768 1.80041e-06ZM14.819 1.43069e-07C15.5365 2.05795e-07 16.0423 0.546002 16.0423 1.1585C16.0423 1.59425 15.7885 2.0615 15.1725 2.38875C12.0435 4.046 8.421 9.78425 8.421 13.3227C13.6972 13.1617 15.9915 17.8412 15.9915 20.7742C15.9915 24.2847 12.9482 28 8.4525 28C2.78075 28 -3.42503e-06 23.5427 -3.00278e-06 18.7127C-2.08775e-06 8.246 11.9578 -1.07069e-07 14.819 1.43069e-07Z"
                  fill="#FF7118"
                />
              </svg>
              <span class="">If your website isn’t converting the traffic you’re already getting, you’re likely leaving revenue on the table.</span>
              <svg
                width="35"
                height="28"
                viewBox="0 0 35 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="inline pt-1"
              >
                <path
                  d="M1.22325 28C0.504 28 0 27.454 0 26.8415C0 26.4057 0.25375 25.9385 0.86975 25.6112C3.99875 23.954 7.62125 18.2157 7.62125 14.6772C2.34325 14.8382 0.0507501 10.1587 0.0507501 7.22575C0.0507501 3.71525 3.09225 0 7.58975 0C13.2615 0 16.0423 4.45725 16.0423 9.28725C16.0423 19.754 4.08275 28 1.22325 28ZM20.181 28C19.4635 28 18.9578 27.454 18.9578 26.8415C18.9578 26.4057 19.2115 25.9385 19.8275 25.6112C22.9565 23.954 26.579 18.2157 26.579 14.6772C21.3028 14.8382 19.0085 10.1587 19.0085 7.22575C19.0085 3.71525 22.0518 0 26.5475 0C32.2193 0 35 4.45725 35 9.28725C35 19.754 23.0423 28 20.181 28Z"
                  fill="#FF7118"
                />
              </svg>
            </motion.p>
          </div>
        </div>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 pt-6 text-slate-700"
        >
          Restaurant websites often get traffic but fail to convert because diners can’t quickly find what they need or take the next step.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Today, <a
            href="https://marketingltb.com/blog/statistics/restaurant-marketing-statistics/"
            target="_blank"
            class="text-[#FF7118] underline"
          >88% of diners search online before choosing where to eat</a>. But many restaurant websites act like digital brochures instead of conversion tools.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          When guests land on your site, they’re not browsing—they’re deciding.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          If your menu, hours, reservations, or ordering options aren’t immediately clear, they leave, and book somewhere else.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          In 2026, restaurant website design isn’t about aesthetics. It’s about speed, clarity, and making the next step effortless.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Because even small confusion can cost you tonight’s covers.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          Why Do Restaurant Websites Get Traffic but Not Customers?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Restaurant websites get traffic but fail to convert because they don’t clearly guide diners to take action.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Most websites lose customers when:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li><span>Key information is hard to find</span></li>
          <li>
            <span>
              Calls to action are unclear or missing

            </span>
          </li>
          <li>
            <span>
              The site loads slowly on mobile

            </span>
          </li>
          <li>
            <span>
              Menus, hours, or photos are outdated

            </span>
          </li>
          <li>
            <span>
              The next step (reserve, order, inquire) isn’t obvious

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Diners don’t spend time figuring things out. They choose the restaurant that makes the decision easiest.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          What Information Do Diners Expect on a Restaurant Website?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Diners visit restaurant websites to quickly find specific information that helps them decide where to eat.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Most diners look for:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li><span>Menu</span></li>
          <li>
            <span>
              Reservations or online ordering

            </span>
          </li>
          <li>
            <span>
              Hours of operation

            </span>
          </li>
          <li>
            <span>
              Location and directions

            </span>
          </li>
          <li>
            <span>
              Private dining or catering information
            </span>
          </li>
          <li>
            <span>
              Photos of food and atmosphere
            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          High-performing restaurant websites answer these questions immediately, without requiring scrolling or searching.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          If someone searches “private dining near me” and can’t find that page instantly, they don’t dig deeper. They leave.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          This is why aligning your website with your Google Business Profile optimization strategy is critical, so diners can move seamlessly from search to decision.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          Are Your Calls to Action Clear Within 5 Seconds?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Clear calls to action (CTAs) are one of the most important factors in restaurant website conversions.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          If diners can’t immediately see how to:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>Make a reservation
            </span>
          </li>
          <li>
            <span>Order online
            </span>
          </li>
          <li>
            <span>Submit a catering request
            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          —they leave.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          High-converting restaurant websites use:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>One primary CTA per page
            </span>
          </li>
          <li>
            <span>Buttons placed above the fold

            </span>
          </li>
          <li>
            <span>High-contrast, easy-to-click designs

            </span>
          </li>
          <li>
            <span>Repeated CTAs throughout the page

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          The goal is simple: make the next step obvious and effortless.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          Does Your Website Build Trust Fast Enough?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          <RouterLink :to="{ name: '/how-it-works' }">
            <span class="text-[#FF7118] underline">Restaurant websites convert better</span>
          </RouterLink>  when they quickly build trust through visuals, reviews, and accurate information.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Diners trust websites that include:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>Recent, high-quality food photography
            </span>
          </li>
          <li>
            <span>
              Visible Google reviews and ratings

            </span>
          </li>
          <li>
            <span>
              Up-to-date menus and hours

            </span>
          </li>
          <li>
            <span>
              Clear contact and location information

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Outdated content or missing information creates hesitation.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          And hesitation leads to lost customers.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          Is Your Restaurant Website Fast and Mobile-Friendly?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Mobile speed is critical because most diners search on their phones shortly before deciding where to eat.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          If your website:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>Loads slowly

            </span>
          </li>
          <li>
            <span>
              Feels cluttered

            </span>
          </li>
          <li>
            <span>
              Requires zooming

            </span>
          </li>
          <li>
            <span>
              Hides important information

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Diners leave immediately.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          A high-performing mobile website should:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>Load in under 3 seconds

            </span>
          </li>
          <li>
            <span>
              Display menus and CTAs clearly

            </span>
          </li>
          <li>
            <span>
              Be easy to navigate with one hand

            </span>
          </li>
          <li>
            <span>
              Prioritize key information at the top

            </span>
          </li>
        </motion.ul>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          What Actually Drives Restaurant Website Conversions?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Restaurant website conversions happen when diners get clear answers, trust your business, and can take action instantly.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          <RouterLink :to="{ name: '/how-it-works' }">
            <span class="text-[#FF7118] underline">Websites convert more visitors</span>
          </RouterLink>  when they:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>Clearly display menu, hours, and location

            </span>
          </li>
          <li>
            <span>
              Show strong reviews and photos

            </span>
          </li>
          <li>
            <span>
              Load quickly on mobile devices

            </span>
          </li>
          <li>
            <span>
              Use simple, intuitive navigation

            </span>
          </li>
          <li>
            <span>
              Make the next step obvious (reserve, order, inquire)

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          When these elements work together, traffic turns into paying customers.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          When they don’t, you lose business without even realizing it.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          How to Turn Restaurant Website Traffic Into Customers?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          To turn website visitors into customers, restaurants must focus on clarity, speed, and ease of action.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Follow these steps:
        </motion.p>
        <motion.ol
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          style="list-style: auto;"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside py-4 text-slate-700"
        >
          <li>
            <span>Make your menu, hours, and location immediately visible

            </span>
          </li>
          <li>
            <span>
              Add a clear primary CTA on every page

            </span>
          </li>
          <li>
            <span>
              Improve mobile speed and usability

            </span>
          </li>
          <li>
            <span>
              Showcase reviews and high-quality photos

            </span>
          </li>
          <li>
            <span>
              Keep all information updated and accurate

            </span>
          </li>
        </motion.ol>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Even small improvements can significantly increase reservations, orders, and inquiries.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          How Your Website Impacts Restaurant SEO and Local Search?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Your website plays a critical role in restaurant SEO because it helps Google determine your visibility in local search results.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          When your website is:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>Fast

            </span>
          </li>
          <li>
            <span>
              Mobile-friendly

            </span>
          </li>
          <li>
            <span>
              Clearly structured

            </span>
          </li>
          <li>
            <span>
              Aligned with your Google Business Profile

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          —it becomes easier for Google to rank your restaurant.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Better rankings mean:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>More visibility

            </span>
          </li>
          <li>
            <span>
              More clicks

            </span>
          </li>
          <li>
            <span>
              More customers

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Your website isn’t just a marketing tool. It’s a core part of your local search strategy.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          How Can You Turn More Website Visitors Into Guests?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          If your website isn’t converting the traffic you’re already getting, you’re likely leaving revenue on the table.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          <RouterLink :to="{ name: '/how-it-works' }">
            <span class="text-[#FF7118] underline">SpotHopper</span>
          </RouterLink>  combines:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>High-performing restaurant website design

            </span>
          </li>
          <li>
            <span>
              Built-in restaurant SEO

            </span>
          </li>
          <li>
            <span>
              Google Business Profile integration

            </span>
          </li>
          <li>
            <span>
              Automated review management

            </span>
          </li>
          <li>
            <span>
              AI-powered marketing tools

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Designed to help restaurants get:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>More reservations

            </span>
          </li>
          <li>
            <span>
              More catering inquiries

            </span>
          </li>
          <li>
            <span>
              More online orders

            </span>
          </li>
          <li>
            <span>
              Better visibility in local search

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          If you’re curious what higher conversion could look like, we’d be happy to show you. <RouterLink
            class="text-[#FF7118] underline"
            :to="{name:'/free-demo'}"
          >
            Book a quick SpotHopper demo
          </RouterLink> and see how it works behind the scenes.
        </motion.p>
      </section>

      <div class="mb-8 w-full bg-white  pb-8 pt-8 md:rounded-[5rem] ">
        <BlogFAQ :items="blog?.faq" />

        <section
          v-if="lastThreeBlogs.length"
          class="mx-auto mb-8 px-2 lg:max-w-screen-xl 2xl:px-0"
        >
          <motion.h2
            :variants="headlineVariants"
            initial="initial"
            while-in-view="animate"
            class="px-4 pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl xl:px-0"
          >
            Keep learning about <span class="lowercase">{{ blog.category }}</span>
          </motion.h2>
          <div class="grid w-full grid-cols-1 gap-x-6 gap-y-12 px-4 py-6 md:grid-cols-3 xl:px-0">
            <motion.div
              v-for="(item, index) in lastThreeBlogs"
              :key="item.slug"
              :custom="index"
              :variants="blogVariants"
              initial="initial"
              while-in-view="animate"
            >
              <BlogCard v-bind="item" />
            </motion.div>
          </div>
        </section>
        <section class="mx-auto px-2 lg:max-w-screen-xl lg:pb-16 2xl:px-0">
          <div class="relative overflow-clip rounded-3xl px-4 py-10 md:px-8 md:py-20">
            <div class="absolute inset-0 isolate before:absolute before:inset-0 before:z-[1] before:bg-gray-800/70">
              <motion.img
                :initial="{ opacity: 0, y: 50 }"
                :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } }"
                :src="`${S3_ASSETS_URL}/home/cta-02.jpg`"
                class="aspect-video size-full object-cover object-center"
                role="presentation"
              />
            </div>

            <div class="isolate grid place-items-center gap-5">
              <motion.h3
                :variants="headlineVariants"
                initial="initial"
                while-in-view="animate"
                class="max-w-screen-sm text-balance text-center text-3xl leading-tight text-white lg:text-5xl"
              >
                Ready to take your restaurant marketing to the next level?
              </motion.h3>

              <UiButton
                class="group w-full max-w-xs lg:max-w-max"
                as-child
              >
                <RouterLink :to="{ name: '/free-demo' }">
                  Book a Free Demo

                  <UiIcon
                    name="arrow-forward-rounded"
                    class="transition-transform duration-200 group-hover:translate-x-1"
                  />
                </RouterLink>
              </UiButton>
            </div>
          </div>
        </section>
      </div>

      <LayoutFooter />
    </div>
  </div>
</template>
